import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { quotes as quotesApi, business as bizApi } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { IDS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Send, Edit3, Copy, Loader2, CheckCircle2, XCircle, Clock,
  Eye, Mail, Phone, MapPin, FileText, History, Wallet, CalendarClock, Image as ImageIcon,
  Download,
} from "lucide-react";
import { format } from "date-fns";

const StatusPill = ({ s }) => {
  const map = {
    draft: ["Draft", "bg-slate-100 text-[#334155]"],
    sent: ["Sent", "bg-blue-50 text-[#2563EB]"],
    viewed: ["Viewed", "bg-indigo-50 text-[#4F46E5]"],
    accepted: ["Accepted", "bg-green-50 text-[#10B981]"],
    declined: ["Declined", "bg-red-50 text-[#EF4444]"],
    modification_requested: ["Modification Requested", "bg-amber-50 text-[#EA580C]"],
  };
  const [label, cls] = map[s] || [s, "bg-slate-100"];
  return (
    <span data-testid={IDS.detail.statusBadge}
          className={`px-3 py-1 text-xs rounded-full uppercase tracking-[0.15em] font-semibold ${cls}`}>
      {label}
    </span>
  );
};

export default function QuoteDetail() {
  const { id: quoteId } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      quotesApi.get(quoteId),
      quotesApi.versions(quoteId),
    ])
      .then(([qr, vr]) => {
        setQ(qr.data);
        setVersions(vr.data || []);
      })
      .catch(() => toast.error("Quote not found"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [quoteId]);

  const publicLink = q ? `${window.location.origin}/q/${q.public_id}` : "";

  const send = async () => {
    if (!q.customer_email) return toast.error("Add a customer email before sending");
    setSending(true);
    try {
      await quotesApi.send(q.quote_id);
      toast.success("Sent to customer");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(publicLink);
    toast.success("Public link copied");
  };

  const downloadPdf = () => {
    // Opens the customer-facing quote and auto-triggers the browser's Save-as-PDF dialog.
    window.open(`${publicLink}?print=1`, "_blank", "noopener");
  };

  if (loading || !q) return (
    <div className="min-h-[400px] flex items-center justify-center">
      <Loader2 className="animate-spin text-[#2563EB]" size={28} />
    </div>
  );

  const canEdit = !["accepted", "declined"].includes(q.status);
  const canSend = q.customer_email && q.status !== "accepted" && q.status !== "declined";

  return (
    <div data-testid={IDS.detail.page} className="p-5 sm:p-8 max-w-6xl mx-auto pb-24">
      <Link to="/quotes" className="inline-flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#001F3F] mb-6">
        <ArrowLeft size={16} /> All quotes
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 text-xs font-mono text-[#64748B]">
            <span>{q.quote_number}</span>
            <span>·</span>
            <span>v{q.version}</span>
            <StatusPill s={q.status} />
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-[#001F3F]"
              style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
            {q.title}
          </h1>
          {q.description && (
            <p className="mt-2 text-sm text-[#64748B] max-w-2xl">{q.description}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid={IDS.detail.publicLinkCopy}
            onClick={copyLink} variant="outline"
            className="rounded-full border-slate-300 h-10 text-[#001F3F]"
          >
            <Copy size={14} className="mr-2" /> Copy link
          </Button>
          <Button
            data-testid="quote-detail-download-pdf"
            onClick={downloadPdf} variant="outline"
            className="rounded-full border-slate-300 h-10 text-[#001F3F]"
            title="Opens a print-ready view — use your browser's Save as PDF"
          >
            <Download size={14} className="mr-2" /> PDF
          </Button>
          {canEdit && (
            <Button
              data-testid={IDS.detail.editBtn}
              onClick={() => nav(`/quotes/${q.quote_id}/edit`)}
              variant="outline" className="rounded-full border-slate-300 h-10 text-[#001F3F]"
            >
              <Edit3 size={14} className="mr-2" />
              {["modification_requested", "sent", "viewed"].includes(q.status) ? "New version" : "Edit"}
            </Button>
          )}
          {canSend && (
            <Button
              data-testid={IDS.detail.sendBtn}
              onClick={send} disabled={sending}
              className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-10 px-5"
            >
              <Send size={14} className="mr-2" /> {q.status === "draft" ? "Send" : "Resend"}
            </Button>
          )}
        </div>
      </div>

      {q.status === "modification_requested" && (
        <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <Clock className="text-[#EA580C] shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="text-sm font-semibold text-[#78350F]">Modification requested</div>
              <div className="mt-1 text-sm text-[#78350F] leading-relaxed">{q.response_feedback}</div>
              <div className="mt-2 text-[11px] uppercase tracking-widest text-[#B45309]">
                — {q.response_customer_name || q.customer_name}
                {q.responded_at ? ` · ${format(new Date(q.responded_at), "MMM d, yyyy HH:mm")}` : ""}
              </div>
              <Button onClick={() => nav(`/quotes/${q.quote_id}/edit`)}
                      className="mt-3 h-9 rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white">
                Create updated version
              </Button>
            </div>
          </div>
        </div>
      )}
      {q.status === "accepted" && (
        <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-[#10B981] shrink-0 mt-0.5" size={20} />
            <div>
              <div className="text-sm font-semibold text-[#065F46]">
                Accepted by {q.response_customer_name || q.customer_name}
              </div>
              <div className="mt-1 text-[11px] uppercase tracking-widest text-[#047857]">
                {q.responded_at ? format(new Date(q.responded_at), "MMM d, yyyy HH:mm") : ""}
              </div>
            </div>
          </div>
        </div>
      )}

      {q.deposit_required && (
        <div className={`mt-4 rounded-lg border p-5 ${
          q.deposit_status === "paid" ? "border-emerald-200 bg-emerald-50" : "border-blue-200 bg-blue-50"
        }`}>
          <div className="flex items-start gap-3">
            <Wallet className={q.deposit_status === "paid" ? "text-[#10B981]" : "text-[#2563EB]"} size={20} />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold">
                {q.deposit_status === "paid" ? "Deposit paid" : `Deposit required (${Number(q.deposit_percentage).toFixed(0)}%)`}
              </div>
              <div className="mt-1 text-sm text-[#001F3F]">
                {money((q.total || 0) * (q.deposit_percentage || 0) / 100, q.currency)}
                {q.deposit_status === "paid" && q.deposit_paid_at
                  ? ` · paid ${format(new Date(q.deposit_paid_at), "MMM d, yyyy HH:mm")}`
                  : q.deposit_status === "pending" ? " · awaiting payment" : ""}
              </div>
            </div>
          </div>
        </div>
      )}

      {q.scheduled_slot_start && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex items-start gap-3">
            <CalendarClock className="text-[#2563EB]" size={20} />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">
                Customer requested time
              </div>
              <div className="mt-1 text-sm font-semibold text-[#001F3F]">
                {format(new Date(q.scheduled_slot_start), "EEE, MMM d, yyyy · HH:mm")}
              </div>
              {q.scheduled_slot_notes && (
                <div className="mt-1 text-xs text-[#334155]">{q.scheduled_slot_notes}</div>
              )}
            </div>
          </div>
        </div>
      )}
      {q.status === "declined" && (
        <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 p-5">
          <div className="flex items-start gap-3">
            <XCircle className="text-[#EF4444] shrink-0 mt-0.5" size={20} />
            <div>
              <div className="text-sm font-semibold text-[#7F1D1D]">
                Declined by {q.response_customer_name || q.customer_name}
              </div>
              <div className="mt-1 text-sm text-[#7F1D1D]">Reason: <b>{q.response_reason}</b></div>
              {q.response_feedback && (
                <div className="mt-1 text-sm text-[#7F1D1D]">{q.response_feedback}</div>
              )}
              <div className="mt-2 text-[11px] uppercase tracking-widest text-[#B91C1C]">
                {q.responded_at ? format(new Date(q.responded_at), "MMM d, yyyy HH:mm") : ""}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">
              Line items
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-[11px] uppercase tracking-[0.15em] text-[#64748B]">
                <tr>
                  <th className="text-left px-5 py-2.5">Description</th>
                  <th className="text-right px-5 py-2.5 w-16">Qty</th>
                  <th className="text-right px-5 py-2.5 w-28">Unit</th>
                  <th className="text-right px-5 py-2.5 w-28">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(q.line_items || []).map((li) => (
                  <tr key={li.id}>
                    <td className="px-5 py-3 text-[#001F3F]">{li.description}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-[#334155]">{li.quantity}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-[#334155]">
                      {money(li.unit_price, q.currency)}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold">
                      {money(li.total, q.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="text-sm">
                <tr><td colSpan={3} className="px-5 py-2 text-right text-[#64748B]">Subtotal</td>
                  <td className="px-5 py-2 text-right tabular-nums">{money(q.subtotal, q.currency)}</td></tr>
                {q.discount > 0 && (
                  <tr><td colSpan={3} className="px-5 py-2 text-right text-[#64748B]">Discount</td>
                    <td className="px-5 py-2 text-right tabular-nums">− {money(q.discount, q.currency)}</td></tr>
                )}
                <tr><td colSpan={3} className="px-5 py-2 text-right text-[#64748B]">Tax ({q.tax_rate}%)</td>
                  <td className="px-5 py-2 text-right tabular-nums">{money(q.tax_amount, q.currency)}</td></tr>
                <tr className="border-t border-slate-100">
                  <td colSpan={3} className="px-5 py-3 text-right font-bold text-[#001F3F]">Total</td>
                  <td className="px-5 py-3 text-right tabular-nums font-black text-lg text-[#001F3F]">
                    {money(q.total, q.currency)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </section>

          {q.terms && (
            <section className="rounded-lg border border-slate-200 bg-white p-6">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-2">
                Terms &amp; conditions
              </div>
              <div className="text-sm text-[#334155] leading-relaxed whitespace-pre-wrap">{q.terms}</div>
            </section>
          )}

          {q.photos && q.photos.length > 0 && (
            <section className="rounded-lg border border-slate-200 bg-white p-6">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-3 flex items-center gap-2">
                <ImageIcon size={12} /> Photos ({q.photos.length})
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {q.photos.map((p) => (
                  <a
                    key={p.id}
                    href={quotesApi.photoUrl(q.quote_id, p.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-md overflow-hidden border border-slate-200 bg-slate-50 block hover:opacity-90 transition-opacity"
                  >
                    <div className="aspect-square">
                      <img src={quotesApi.photoUrl(q.quote_id, p.id)} alt={p.caption || p.filename || "photo"} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    {p.caption && (
                      <div className="px-2 py-1.5 bg-white border-t border-slate-200 text-[10px] leading-tight text-[#334155] line-clamp-2">
                        {p.caption}
                      </div>
                    )}
                  </a>
                ))}
              </div>
            </section>
          )}

          {q.internal_notes && (
            <section className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-2 flex items-center gap-2">
                <FileText size={12} /> Internal notes (hidden from customer)
              </div>
              <div className="text-sm text-[#334155] leading-relaxed whitespace-pre-wrap">{q.internal_notes}</div>
            </section>
          )}
        </div>

        <div className="space-y-6">
          <section className="rounded-lg border border-slate-200 bg-white p-6">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-3">Customer</div>
            <div className="text-lg font-bold text-[#001F3F]">{q.customer_name || "—"}</div>
            <div className="mt-2 space-y-1 text-sm text-[#334155]">
              {q.customer_email && <div className="flex items-center gap-2"><Mail size={14} className="text-[#64748B]" />{q.customer_email}</div>}
              {q.customer_phone && <div className="flex items-center gap-2"><Phone size={14} className="text-[#64748B]" />{q.customer_phone}</div>}
              {q.customer_address && <div className="flex items-center gap-2"><MapPin size={14} className="text-[#64748B]" />{q.customer_address}</div>}
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-6">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-3">Public link</div>
            <div className="text-xs text-[#334155] break-all bg-slate-50 border border-slate-200 rounded p-2 font-mono">
              {publicLink}
            </div>
            <Button onClick={copyLink} variant="outline"
                    className="mt-3 w-full h-9 rounded-full border-slate-300 text-[#001F3F]">
              <Copy size={14} className="mr-2" /> Copy link
            </Button>
            <a href={publicLink} target="_blank" rel="noreferrer"
               className="mt-2 text-xs text-[#2563EB] hover:underline inline-flex items-center gap-1">
              <Eye size={12} /> Preview customer view
            </a>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-6">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-3 flex items-center gap-2">
              <History size={12} /> Version history
            </div>
            <div className="space-y-2" data-testid={IDS.detail.responsesList}>
              {versions.map((v) => (
                <div key={v.quote_id}
                     className={`flex items-center justify-between text-xs py-2 border-b border-slate-100 last:border-0
                       ${v.quote_id === q.quote_id ? "font-bold text-[#2563EB]" : "text-[#334155]"}`}>
                  <span>v{v.version}</span>
                  <span className="uppercase tracking-widest">{v.status}</span>
                  <span className="text-[#64748B]">
                    {v.created_at ? format(new Date(v.created_at), "MMM d") : ""}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
